import type {
  AsyncOperation,
  OperationAccepted,
  UploadCompleteRequest,
  UploadPresignRequest,
  UploadSession,
} from '../api/active/generated/equipment';
import { randomUuid } from '../api/active/randomUuid';
import type { ActiveTransport } from '../api/active/transport';

export class CatalogMediaApiError extends Error {
  constructor(
    readonly status: number | null,
    readonly code: string | undefined,
    message: string,
  ) {
    super(message);
  }
}

export type CatalogMediaPurpose = UploadPresignRequest['purpose'];
export type CatalogUploadPresignRequest = Omit<
  UploadPresignRequest,
  'purpose'
> & { purpose: CatalogMediaPurpose };

async function problem(result: Response): Promise<CatalogMediaApiError> {
  let value: {
    code?: string;
    errorCode?: string;
    detail?: string;
    title?: string;
  } = {};
  try {
    value = (await result.json()) as typeof value;
  } catch {
    // Preserve the HTTP failure when an upstream proxy returns no JSON.
  }
  return new CatalogMediaApiError(
    result.status,
    value.code ?? value.errorCode,
    value.detail ?? value.title ?? `媒体请求失败（HTTP ${result.status}）。`,
  );
}

export function createCatalogMediaApi(
  transport: ActiveTransport,
  csrfToken: string | null,
) {
  const localDemoTransport = transport as ActiveTransport & {
    localDemoMedia?: true;
    putLocalDemoObject?: (
      objectId: string,
      file: File,
      signal?: AbortSignal,
    ) => Promise<string>;
  };
  const mutationHeaders: Record<string, string> = csrfToken
    ? { 'X-CSRF-Token': csrfToken }
    : {};
  const write = async <T>(
    path: string,
    body: unknown,
    options: {
      etag?: string;
      idempotencyKey?: string;
      signal?: AbortSignal;
    } = {},
  ): Promise<{ value: T; etag: string }> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Idempotency-Key': options.idempotencyKey ?? randomUuid(),
      ...mutationHeaders,
    };
    if (options.etag) headers['If-Match'] = options.etag;
    const result = await transport.requestResponse(path, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: options.signal,
    });
    if (!result.ok) throw await problem(result);
    return {
      value: (await result.json()) as T,
      etag: result.headers.get('ETag') ?? '',
    };
  };
  return {
    createUploadTicket: (
      body: CatalogUploadPresignRequest,
      idempotencyKey?: string,
      signal?: AbortSignal,
    ) =>
      write<UploadSession>('/files/upload-tickets', body, {
        idempotencyKey,
        signal,
      }),
    putObject: async (
      session: UploadSession,
      file: File,
      signal?: AbortSignal,
    ) => {
      if (session.uploadUrl.startsWith('cpq-local-demo://')) {
        const offlineDemoEnabled =
          import.meta.env.DEV &&
          (
            import.meta.env as ImportMetaEnv & {
              VITE_CPQ_OFFLINE_DEMO?: string;
            }
          ).VITE_CPQ_OFFLINE_DEMO === 'true';
        const localHost =
          typeof window !== 'undefined' &&
          ['localhost', '127.0.0.1'].includes(window.location.hostname);
        const expectedUrl = `cpq-local-demo://${session.objectId}`;
        if (
          !offlineDemoEnabled ||
          !localHost ||
          localDemoTransport.localDemoMedia !== true ||
          typeof localDemoTransport.putLocalDemoObject !== 'function' ||
          session.uploadUrl !== expectedUrl
        )
          throw new CatalogMediaApiError(
            null,
            'OFFLINE_DEMO_MEDIA_UNAVAILABLE',
            '本地图片演练适配器不可用，未发送网络请求。',
          );
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        return localDemoTransport.putLocalDemoObject(
          session.objectId,
          file,
          signal,
        );
      }
      const declaredLength = session.requiredHeaders.find(
        ({ name }) => name.toLowerCase() === 'content-length',
      )?.value;
      if (declaredLength !== undefined && Number(declaredLength) !== file.size)
        throw new CatalogMediaApiError(
          null,
          'OSS_CONTENT_LENGTH_MISMATCH',
          '上传票据的文件大小与所选文件不一致。',
        );
      const headers = Object.fromEntries(
        session.requiredHeaders
          .filter(({ name }) => name.toLowerCase() !== 'content-length')
          .map(({ name, value }) => [name, value]),
      );
      let result: Response;
      try {
        result = await fetch(session.uploadUrl, {
          method: 'PUT',
          headers,
          body: file,
          signal,
        });
      } catch (error) {
        if (error instanceof TypeError)
          throw new CatalogMediaApiError(
            null,
            'OSS_NETWORK_FAILED',
            '图片无法连接对象存储，请检查网络或联系管理员检查跨域设置。',
          );
        throw error;
      }
      if (!result.ok)
        throw new CatalogMediaApiError(
          result.status,
          'OSS_UPLOAD_FAILED',
          `图片上传失败（HTTP ${result.status}）。`,
        );
      const etag = result.headers.get('ETag')?.replace(/^"|"$/g, '');
      if (!etag)
        throw new CatalogMediaApiError(
          null,
          'OSS_ETAG_MISSING',
          '对象存储未返回 ETag，请联系管理员检查跨域响应头。',
        );
      return etag;
    },
    completeUpload: (
      objectId: string,
      etag: string,
      body: UploadCompleteRequest,
      idempotencyKey?: string,
      signal?: AbortSignal,
    ) =>
      write<OperationAccepted>(
        `/files/${encodeURIComponent(objectId)}:complete`,
        body,
        { etag, idempotencyKey, signal },
      ),
    async getAsyncOperation(operationId: string, signal?: AbortSignal) {
      const result = await transport.requestResponse(
        `/operations/${encodeURIComponent(operationId)}`,
        { signal },
      );
      if (!result.ok) throw await problem(result);
      return (await result.json()) as AsyncOperation;
    },
  };
}

export type CatalogMediaApi = ReturnType<typeof createCatalogMediaApi>;
